import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ChevronLeft,
  Brain,
  Building,
  Scale,
  FileText,
  AlertCircle,
  Hash,
  MapPin,
  AlertTriangle,
  BarChart3,
  Activity,
  TrendingUp,
  TrendingDown,
  History,
  CheckCircle2,
  XCircle,
  Flag,
  Send,
  Printer,
  Download,
} from 'lucide-react';
import { usePayments } from '../../hooks/usePayments';
import { useTheme } from '../../context/ThemeContext';
import { getThemeClasses } from '../../utils/theme';
import PriorityBadge from '../common/PriorityBadge';
import { formatCurrency, formatDate } from '../../utils/formatters';
import Footer from '../common/Footer';

// Sub-components
const RiskScoreLarge = ({ score }) => {
  let color = 'from-green-500 to-green-600';
  let bgColor = 'bg-green-500/20';
  let textColor = 'text-green-400';
  let label = 'Low Risk';

  if (score >= 70) {
    color = 'from-red-500 to-red-600';
    bgColor = 'bg-red-500/20';
    textColor = 'text-red-400';
    label = 'High Risk';
  } else if (score >= 50) {
    color = 'from-amber-500 to-amber-600';
    bgColor = 'bg-amber-500/20';
    textColor = 'text-amber-400';
    label = 'Medium Risk';
  }

  return (
    <div className={`${bgColor} rounded-xl p-4 text-center`}>
      <div
        className={`inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br ${color} text-white text-3xl font-bold shadow-lg`}
      >
        {score}
      </div>
      <p className={`mt-2 text-sm font-medium ${textColor}`}>{label}</p>
    </div>
  );
};

const SeverityBadge = ({ severity }) => {
  const styles = {
    HIGH: 'bg-red-500/20 text-red-400',
    MEDIUM: 'bg-amber-500/20 text-amber-400',
    LOW: 'bg-blue-500/20 text-blue-400',
  };
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium ${styles[severity]}`}>
      {severity}
    </span>
  );
};

const FeatureContributionBar = ({ contribution, direction, description }) => {
  const { isDarkMode } = useTheme();
  const theme = getThemeClasses(isDarkMode);
  const width = Math.min(contribution * 100 * 3, 100);
  return (
    <div className="flex items-center gap-3">
      <div className="flex-1">
        <div className="flex items-center justify-between mb-1">
          <span className={`text-sm ${theme.textSecondary}`}>{description}</span>
          <span className={`text-sm font-medium ${theme.textPrimary}`}>
            {(contribution * 100).toFixed(0)}%
          </span>
        </div>
        <div className={`h-2 ${theme.bgTertiary} rounded-full overflow-hidden`}>
          <div
            className={`h-full rounded-full ${
              direction === 'risk' ? 'bg-red-500' : 'bg-green-500'
            }`}
            style={{ width: `${width}%` }}
          />
        </div>
      </div>
    </div>
  );
};

const CaseDetailView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const theme = getThemeClasses(isDarkMode);
  const { getDetailedPaymentById, loading } = usePayments();
  const [activeTab, setActiveTab] = useState('analysis');
  const [decision, setDecision] = useState('');
  const [issueType, setIssueType] = useState('');
  const [issueCategory, setIssueCategory] = useState('');
  const [confidence, setConfidence] = useState('');
  const [overpaymentAmount, setOverpaymentAmount] = useState('');
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const payment = getDetailedPaymentById(id);

  // Show loading spinner while data is being fetched
  if (loading) {
    return (
      <div className={`min-h-screen ${theme.bgPrimary} flex flex-col`}>
        <div className="flex-1 flex items-center justify-center p-6">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 dark:border-blue-400 mb-4"></div>
            <p className={theme.textTertiary}>Loading case details...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // Show "not found" only after loading completes
  if (!payment) {
    return (
      <div className={`min-h-screen ${theme.bgPrimary} flex flex-col`}>
        <div className="flex-1 flex items-center justify-center p-6">
          <div className="text-center">
            <p className={theme.textTertiary}>Case not found</p>
            <button
              onClick={() => navigate('/dashboard')}
              className="mt-4 px-4 py-2 text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-500/20 rounded-lg transition"
            >
              Return to Dashboard
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const handleSubmitDecision = () => {
    setIsSubmitting(true);
    setTimeout(() => {
      setIsSubmitting(false);
      alert('Decision submitted successfully!');
      navigate('/dashboard');
    }, 1500);
  };

  const tabs = [
    { id: 'analysis', label: 'AI Analysis', icon: Brain },
    { id: 'provider', label: 'Provider History', icon: Building },
    { id: 'similar', label: 'Similar Cases', icon: Scale },
    { id: 'documents', label: 'Documents', icon: FileText },
  ];

  return (
    <div className={`min-h-screen ${theme.bgPrimary} flex flex-col`}>
      <div className="flex-1">
        {/* Main Content */}
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          {/* Back Button & Actions */}
          <div className="flex items-center justify-between mb-6">
            <button
              onClick={() => navigate('/dashboard')}
              className={`inline-flex items-center gap-2 ${theme.textSecondary} hover:${theme.textPrimary} transition`}
            >
              <ChevronLeft className="w-5 h-5" />
              <span>Back to Queue</span>
            </button>
            <div className="flex items-center gap-2">
              <button className={`inline-flex items-center gap-2 px-3 py-2 text-sm ${theme.textSecondary} ${theme.bgHover} rounded-lg transition`}>
                <Printer className="w-4 h-4" />
                Print
              </button>
              <button className={`inline-flex items-center gap-2 px-3 py-2 text-sm ${theme.textSecondary} ${theme.bgHover} rounded-lg transition`}>
                <Download className="w-4 h-4" />
                Export
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Column - Payment Details & Analysis */}
            <div className="lg:col-span-2 space-y-6">
              {/* Payment Header Card */}
              <div className={`${theme.card} rounded-xl border shadow-sm overflow-hidden`}>
                <div className="p-6">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 flex-wrap">
                        <h2 className={`text-xl font-bold ${theme.textPrimary}`}>
                          {payment.provider?.name || payment.provider}
                        </h2>
                        <PriorityBadge priority={payment.priority} />
                        <span className={`text-xs ${theme.textMuted} ${theme.bgTertiary} px-2 py-1 rounded`}>
                          {payment.auditType}
                        </span>
                      </div>
                      <div className={`flex items-center gap-4 mt-2 text-sm ${theme.textTertiary} flex-wrap`}>
                        <span className="flex items-center gap-1">
                          <Hash className="w-4 h-4" />
                          {payment.id}
                        </span>
                        <span className="flex items-center gap-1">
                          <Building className="w-4 h-4" />
                          NPI: {payment.provider?.npi || 'N/A'}
                        </span>
                        <span className="flex items-center gap-1">
                          <MapPin className="w-4 h-4" />
                          {payment.region}
                        </span>
                      </div>
                    </div>
                    <RiskScoreLarge score={payment.riskScore} />
                  </div>

                  {/* Payment Amount Summary */}
                  {payment.payment && (
                    <div className={`grid grid-cols-3 gap-4 mt-6 p-4 ${theme.bgTertiary} rounded-lg`}>
                      <div>
                        <p className={`text-xs ${theme.textTertiary} uppercase tracking-wide`}>
                          Requested
                        </p>
                        <p className={`text-xl font-bold ${theme.textPrimary}`}>
                          {formatCurrency(payment.payment.requestedAmount)}
                        </p>
                      </div>
                      <div>
                        <p className={`text-xs ${theme.textTertiary} uppercase tracking-wide`}>
                          Allowed
                        </p>
                        <p className="text-xl font-bold text-green-600 dark:text-green-400">
                          {formatCurrency(payment.payment.allowedAmount)}
                        </p>
                      </div>
                      <div>
                        <p className={`text-xs ${theme.textTertiary} uppercase tracking-wide`}>
                          Overpayment Risk
                        </p>
                        <p className="text-xl font-bold text-red-600 dark:text-red-400">
                          {formatCurrency(
                            payment.payment.requestedAmount -
                              payment.payment.allowedAmount
                          )}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Service Details */}
                  {payment.payment && (
                    <div className="mt-6">
                      <h4 className={`text-sm font-medium ${theme.textSecondary} mb-3`}>
                        Service Details
                      </h4>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <p className={theme.textTertiary}>Service Date</p>
                          <p className={`font-medium ${theme.textPrimary}`}>
                            {formatDate(payment.payment.serviceDate)}
                          </p>
                        </div>
                        <div>
                          <p className={theme.textTertiary}>Submission Date</p>
                          <p className={`font-medium ${theme.textPrimary}`}>
                            {formatDate(payment.payment.submissionDate)}
                          </p>
                        </div>
                        <div>
                          <p className={theme.textTertiary}>Place of Service</p>
                          <p className={`font-medium ${theme.textPrimary}`}>
                            {payment.payment.placeOfService}
                          </p>
                        </div>
                        <div>
                          <p className={theme.textTertiary}>Beneficiary</p>
                          <p className={`font-medium ${theme.textPrimary}`}>
                            {payment.beneficiary?.id || 'N/A'}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Procedure Codes */}
                  {payment.payment?.procedureCodes && (
                    <div className="mt-6">
                      <h4 className={`text-sm font-medium ${theme.textSecondary} mb-3`}>
                        Procedure Codes
                      </h4>
                      <div className="space-y-2">
                        {payment.payment.procedureCodes.map((proc, idx) => (
                          <div
                            key={idx}
                            className={`flex items-center justify-between p-3 rounded-lg ${
                              proc.flagged
                                ? 'bg-red-50 dark:bg-red-500/20 border border-red-200 dark:border-red-500/30'
                                : theme.bgTertiary
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              {proc.flagged && (
                                <AlertTriangle className="w-4 h-4 text-red-500 dark:text-red-400" />
                              )}
                              <div>
                                <span className={`font-mono font-medium ${proc.flagged ? 'text-slate-900 dark:text-slate-100' : theme.textPrimary}`}>
                                  {proc.code}
                                </span>
                                <p className={`text-sm ${proc.flagged ? 'text-slate-700 dark:text-slate-300' : theme.textTertiary}`}>
                                  {proc.description}
                                </p>
                              </div>
                            </div>
                            <span className={`font-medium ${theme.textPrimary}`}>
                              {formatCurrency(proc.amount)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Diagnosis Codes */}
                  {payment.payment?.diagnosisCodes && (
                    <div className="mt-4">
                      <h4 className={`text-sm font-medium ${theme.textSecondary} mb-2`}>
                        Diagnosis Codes
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {payment.payment.diagnosisCodes.map((code, idx) => (
                          <span
                            key={idx}
                            className={`font-mono text-sm ${theme.bgTertiary} ${theme.textPrimary} px-2 py-1 rounded`}
                          >
                            {code}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Tabs Section */}
              <div className={`${theme.card} rounded-xl border shadow-sm overflow-hidden`}>
                {/* Tab Headers */}
                <div className={`border-b ${theme.border}`}>
                  <div className="flex">
                    {tabs.map((tab) => (
                      <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`flex items-center gap-2 px-6 py-4 text-sm font-medium border-b-2 transition ${
                          activeTab === tab.id
                            ? 'border-blue-600 text-blue-600'
                            : `border-transparent ${theme.textTertiary} hover:${theme.textSecondary}`
                        }`}
                      >
                        <tab.icon className="w-4 h-4" />
                        {tab.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Tab Content */}
                <div className="p-6">
                  {/* AI Analysis Tab */}
                  {activeTab === 'analysis' && payment.aiAnalysis && (
                    <div className="space-y-6">
                      {/* AI Summary */}
                      <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                        <div className="flex items-start gap-3">
                          <Brain className="w-5 h-5 text-blue-600 mt-0.5" />
                          <div>
                            <div className="flex items-center gap-2">
                              <h4 className="font-medium text-blue-900">
                                AI Analysis Summary
                              </h4>
                              <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">
                                {(payment.aiAnalysis.confidence * 100).toFixed(0)}%
                                confidence
                              </span>
                            </div>
                            <p className="text-sm text-blue-800 mt-1">
                              {payment.aiAnalysis.summary}
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Rule Violations */}
                      {payment.aiAnalysis.ruleViolations && (
                        <div>
                          <h4 className={`text-sm font-medium ${theme.textSecondary} mb-3 flex items-center gap-2`}>
                            <AlertCircle className="w-4 h-4 text-red-500 dark:text-red-400" />
                            Rule Violations
                          </h4>
                          <div className="space-y-3">
                            {payment.aiAnalysis.ruleViolations.map((rule, idx) => (
                              <div
                                key={idx}
                                className={`p-4 border ${theme.border} rounded-lg`}
                              >
                                <div className="flex items-start justify-between">
                                  <div>
                                    <div className="flex items-center gap-2">
                                      <span className={`font-medium ${theme.textPrimary}`}>
                                        {rule.ruleName}
                                      </span>
                                      <SeverityBadge severity={rule.severity} />
                                    </div>
                                    <p className={`text-xs ${theme.textMuted} mt-0.5`}>
                                      Rule ID: {rule.ruleId}
                                    </p>
                                  </div>
                                </div>
                                <p className={`text-sm ${theme.textSecondary} mt-2`}>
                                  {rule.description}
                                </p>
                                <div className="mt-2 p-2 bg-amber-50 dark:bg-amber-500/20 rounded text-sm text-amber-800 dark:text-amber-300">
                                  <strong>Recommendation:</strong>{' '}
                                  {rule.recommendation}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Statistical Flags */}
                      {payment.aiAnalysis.statisticalFlags && (
                        <div>
                          <h4 className={`text-sm font-medium ${theme.textSecondary} mb-3 flex items-center gap-2`}>
                            <BarChart3 className="w-4 h-4 text-amber-500 dark:text-amber-400" />
                            Statistical Anomalies
                          </h4>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            {payment.aiAnalysis.statisticalFlags.map((stat, idx) => (
                              <div
                                key={idx}
                                className={`p-4 border ${theme.border} rounded-lg`}
                              >
                                <p className={`text-xs ${theme.textTertiary} uppercase tracking-wide`}>
                                  {stat.type}
                                </p>
                                <p className={`text-lg font-bold ${theme.textPrimary} mt-1`}>
                                  {stat.value}
                                </p>
                                <div className="flex items-center gap-2 mt-2">
                                  {stat.change && stat.change.startsWith('+') ? (
                                    <TrendingUp className="w-4 h-4 text-red-500 dark:text-red-400" />
                                  ) : (
                                    <TrendingDown className="w-4 h-4 text-green-500 dark:text-green-400" />
                                  )}
                                  <span className={`text-sm ${theme.textSecondary}`}>
                                    {stat.peerAverage && `Peer avg: ${stat.peerAverage}`}
                                    {stat.change && `${stat.change} over ${stat.period}`}
                                  </span>
                                </div>
                                <p className={`text-xs ${theme.textTertiary} mt-2`}>
                                  {stat.description}
                                </p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* ML Feature Contributions */}
                      {payment.aiAnalysis.mlFeatures && (
                        <div>
                          <h4 className={`text-sm font-medium ${theme.textSecondary} mb-3 flex items-center gap-2`}>
                            <Activity className="w-4 h-4 text-purple-500 dark:text-purple-400" />
                            ML Feature Contributions
                          </h4>
                          <div className={`space-y-4 p-4 ${theme.bgTertiary} rounded-lg`}>
                            {payment.aiAnalysis.mlFeatures.map((feature, idx) => (
                              <FeatureContributionBar key={idx} {...feature} />
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Provider History Tab */}
                  {activeTab === 'provider' && payment.providerHistory && (
                    <div className="space-y-6">
                      {/* Provider Info */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className={`p-4 ${theme.bgTertiary} rounded-lg`}>
                          <p className={`text-xs ${theme.textTertiary}`}>Specialty</p>
                          <p className={`font-medium ${theme.textPrimary}`}>
                            {payment.provider.specialty}
                          </p>
                        </div>
                        <div className={`p-4 ${theme.bgTertiary} rounded-lg`}>
                          <p className={`text-xs ${theme.textTertiary}`}>Years in Practice</p>
                          <p className={`font-medium ${theme.textPrimary}`}>
                            {payment.provider.yearsInPractice}
                          </p>
                        </div>
                        <div className={`p-4 ${theme.bgTertiary} rounded-lg`}>
                          <p className={`text-xs ${theme.textTertiary}`}>Total Claims YTD</p>
                          <p className={`font-medium ${theme.textPrimary}`}>
                            {payment.providerHistory.metrics.claimsYTD.toLocaleString()}
                          </p>
                        </div>
                        <div className={`p-4 ${theme.bgTertiary} rounded-lg`}>
                          <p className={`text-xs ${theme.textTertiary}`}>
                            Prior Audit Findings
                          </p>
                          <p className="font-medium text-red-600 dark:text-red-400">
                            {payment.provider.priorAuditFindings}
                          </p>
                        </div>
                      </div>

                      {/* Provider Metrics */}
                      <div>
                        <h4 className={`text-sm font-medium ${theme.textSecondary} mb-3`}>
                          Performance Metrics
                        </h4>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                          <div className={`p-4 border ${theme.border} rounded-lg`}>
                            <p className={`text-xs ${theme.textTertiary}`}>Total Paid YTD</p>
                            <p className={`text-lg font-bold ${theme.textPrimary}`}>
                              $
                              {(
                                payment.providerHistory.metrics.totalPaidYTD / 1000
                              ).toFixed(0)}
                              K
                            </p>
                          </div>
                          <div className={`p-4 border ${theme.border} rounded-lg`}>
                            <p className={`text-xs ${theme.textTertiary}`}>Avg per Claim</p>
                            <p className={`text-lg font-bold ${theme.textPrimary}`}>
                              ${payment.providerHistory.metrics.averagePerClaim}
                            </p>
                            <p className={`text-xs ${theme.textTertiary}`}>
                              Peer avg: $
                              {payment.providerHistory.metrics.peerAveragePerClaim}
                            </p>
                          </div>
                          <div className={`p-4 border ${theme.border} rounded-lg`}>
                            <p className={`text-xs ${theme.textTertiary}`}>Denial Rate</p>
                            <p className={`text-lg font-bold ${theme.textPrimary}`}>
                              {payment.providerHistory.metrics.denialRate}%
                            </p>
                          </div>
                          <div className={`p-4 border ${theme.border} rounded-lg`}>
                            <p className={`text-xs ${theme.textTertiary}`}>Risk Tier</p>
                            <p className="text-lg font-bold text-amber-600 dark:text-amber-400">
                              {payment.provider.riskTier}
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Recent Claims */}
                      <div>
                        <h4 className={`text-sm font-medium ${theme.textSecondary} mb-3`}>
                          Recent Claims
                        </h4>
                        <div className={`border ${theme.border} rounded-lg overflow-hidden`}>
                          <table className="w-full text-sm">
                            <thead className={theme.bgTertiary}>
                              <tr>
                                <th className={`text-left px-4 py-3 font-medium ${theme.textSecondary}`}>
                                  Date
                                </th>
                                <th className={`text-left px-4 py-3 font-medium ${theme.textSecondary}`}>
                                  Codes
                                </th>
                                <th className={`text-right px-4 py-3 font-medium ${theme.textSecondary}`}>
                                  Amount
                                </th>
                                <th className={`text-right px-4 py-3 font-medium ${theme.textSecondary}`}>
                                  Status
                                </th>
                              </tr>
                            </thead>
                            <tbody className={`divide-y ${theme.borderLight}`}>
                              {payment.providerHistory.recentClaims.map(
                                (claim, idx) => (
                                  <tr key={idx} className={theme.cardHover}>
                                    <td className={`px-4 py-3 ${theme.textPrimary}`}>
                                      {formatDate(claim.date)}
                                    </td>
                                    <td className="px-4 py-3">
                                      <div className="flex flex-wrap gap-1">
                                        {claim.codes.map((code, i) => (
                                          <span
                                            key={i}
                                            className={`font-mono text-xs ${theme.bgTertiary} ${theme.textPrimary} px-1.5 py-0.5 rounded`}
                                          >
                                            {code}
                                          </span>
                                        ))}
                                      </div>
                                    </td>
                                    <td className={`px-4 py-3 text-right font-medium ${theme.textPrimary}`}>
                                      {formatCurrency(claim.amount)}
                                    </td>
                                    <td className="px-4 py-3 text-right">
                                      <span
                                        className={`text-xs px-2 py-1 rounded ${
                                          claim.status === 'Paid'
                                            ? 'bg-green-100 dark:bg-green-500/20 text-green-700 dark:text-green-400'
                                            : 'bg-amber-100 dark:bg-amber-500/20 text-amber-700 dark:text-amber-400'
                                        }`}
                                      >
                                        {claim.status}
                                      </span>
                                    </td>
                                  </tr>
                                )
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>

                      {/* Audit History */}
                      <div>
                        <h4 className={`text-sm font-medium ${theme.textSecondary} mb-3 flex items-center gap-2`}>
                          <History className="w-4 h-4" />
                          Prior Audit Findings
                        </h4>
                        <div className="space-y-3">
                          {payment.providerHistory.auditHistory.map((audit, idx) => (
                            <div
                              key={idx}
                              className="p-4 border border-red-200 dark:border-red-500/30 bg-red-50 dark:bg-red-500/20 rounded-lg"
                            >
                              <div className="flex items-start justify-between">
                                <div>
                                  <p className={`font-medium ${theme.textPrimary}`}>
                                    {audit.finding}
                                  </p>
                                  <p className={`text-sm ${theme.textTertiary}`}>
                                    {formatDate(audit.date)}
                                  </p>
                                </div>
                                <div className="text-right">
                                  <p className="font-medium text-red-600 dark:text-red-400">
                                    {formatCurrency(audit.amount)}
                                  </p>
                                  <p className="text-xs text-green-600 dark:text-green-400">
                                    {audit.resolution}
                                  </p>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Similar Cases Tab */}
                  {activeTab === 'similar' && payment.similarCases && (
                    <div className="space-y-4">
                      <p className={`text-sm ${theme.textTertiary}`}>
                        These cases have similar characteristics and may inform your
                        decision.
                      </p>
                      {payment.similarCases.map((simCase, idx) => (
                        <div
                          key={idx}
                          className={`p-4 border ${theme.border} rounded-lg ${theme.cardHover} transition cursor-pointer`}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <div className="flex items-center gap-3">
                                <span className={`font-medium ${theme.textPrimary}`}>
                                  {simCase.provider}
                                </span>
                                <span
                                  className={`text-xs px-2 py-1 rounded ${
                                    simCase.decision === 'DENIED'
                                      ? 'bg-red-100 dark:bg-red-500/20 text-red-700 dark:text-red-400'
                                      : 'bg-green-100 dark:bg-green-500/20 text-green-700 dark:text-green-400'
                                  }`}
                                >
                                  {simCase.decision}
                                </span>
                              </div>
                              <p className={`text-sm ${theme.textTertiary} mt-1`}>
                                {simCase.id}
                              </p>
                            </div>
                            <div className="text-right">
                              <p className={`font-medium ${theme.textPrimary}`}>
                                {formatCurrency(simCase.amount)}
                              </p>
                              <p className={`text-sm ${theme.textTertiary}`}>
                                Risk: {simCase.riskScore}
                              </p>
                            </div>
                          </div>
                          <p className={`text-sm ${theme.textSecondary} mt-3 p-2 ${theme.bgTertiary} rounded`}>
                            <strong>Finding:</strong> {simCase.finding}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Documents Tab */}
                  {activeTab === 'documents' && (
                    <div className="text-center py-12">
                      <FileText className={`w-12 h-12 ${theme.textMuted} mx-auto mb-4`} />
                      <p className={theme.textTertiary}>
                        Supporting documentation would be displayed here.
                      </p>
                      <p className={`text-sm ${theme.textMuted} mt-2`}>
                        Integration with document management system pending.
                      </p>
                    </div>
                  )}

                  {/* Fallback for cases without detailed data */}
                  {activeTab === 'analysis' && !payment.aiAnalysis && (
                    <div className="text-center py-12">
                      <AlertCircle className={`w-12 h-12 ${theme.textMuted} mx-auto mb-4`} />
                      <p className={theme.textTertiary}>
                        Detailed AI analysis not available for this case.
                      </p>
                    </div>
                  )}

                  {activeTab === 'provider' && !payment.providerHistory && (
                    <div className="text-center py-12">
                      <Building className={`w-12 h-12 ${theme.textMuted} mx-auto mb-4`} />
                      <p className={theme.textTertiary}>
                        Provider history not available for this case.
                      </p>
                    </div>
                  )}

                  {activeTab === 'similar' && !payment.similarCases && (
                    <div className="text-center py-12">
                      <Scale className={`w-12 h-12 ${theme.textMuted} mx-auto mb-4`} />
                      <p className={theme.textTertiary}>
                        Similar cases not available for this payment.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Right Column - Decision Panel */}
            <div className="lg:col-span-1">
              <div className={`${theme.card} rounded-xl border shadow-sm sticky top-6`}>
                <div className={`p-5 border-b ${theme.border}`}>
                  <h3 className={`text-lg font-semibold ${theme.textPrimary}`}>
                    Auditor Decision
                  </h3>
                  <p className={`text-sm ${theme.textTertiary} mt-1`}>
                    Record your findings and recommendation
                  </p>
                </div>

                <div className="p-5 space-y-5">
                  {/* Decision */}
                  <div>
                    <label className={`block text-sm font-medium ${theme.textSecondary} mb-2`}>
                      Decision
                    </label>
                    <div className="grid grid-cols-3 gap-2">
                      <button
                        onClick={() => setDecision('APPROVE')}
                        className={`flex flex-col items-center gap-1 p-3 rounded-lg border-2 transition ${
                          decision === 'APPROVE'
                            ? 'border-green-500 bg-green-50 dark:bg-green-500/20 text-green-700 dark:text-green-400'
                            : `border ${theme.border} hover:border-slate-300 dark:hover:border-slate-600 ${theme.textSecondary}`
                        }`}
                      >
                        <CheckCircle2 className="w-5 h-5" />
                        <span className="text-xs font-medium">Approve</span>
                      </button>
                      <button
                        onClick={() => setDecision('DENY')}
                        className={`flex flex-col items-center gap-1 p-3 rounded-lg border-2 transition ${
                          decision === 'DENY'
                            ? 'border-red-500 bg-red-50 dark:bg-red-500/20 text-red-700 dark:text-red-400'
                            : `border ${theme.border} hover:border-slate-300 dark:hover:border-slate-600 ${theme.textSecondary}`
                        }`}
                      >
                        <XCircle className="w-5 h-5" />
                        <span className="text-xs font-medium">Deny</span>
                      </button>
                      <button
                        onClick={() => setDecision('REFER')}
                        className={`flex flex-col items-center gap-1 p-3 rounded-lg border-2 transition ${
                          decision === 'REFER'
                            ? 'border-amber-500 bg-amber-50 dark:bg-amber-500/20 text-amber-700 dark:text-amber-400'
                            : `border ${theme.border} hover:border-slate-300 dark:hover:border-slate-600 ${theme.textSecondary}`
                        }`}
                      >
                        <Flag className="w-5 h-5" />
                        <span className="text-xs font-medium">Refer</span>
                      </button>
                    </div>
                  </div>

                  {/* Issue Type */}
                  <div>
                    <label className={`block text-sm font-medium ${theme.textSecondary} mb-2`}>
                      Issue Type
                    </label>
                    <select
                      value={issueType}
                      onChange={(e) => setIssueType(e.target.value)}
                      className={`w-full px-3 py-2 ${theme.input} rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none`}
                    >
                      <option value="">Select type...</option>
                      <option value="ERROR">Billing Error</option>
                      <option value="FRAUD">Suspected Fraud</option>
                      <option value="COMPLIANCE">Compliance Issue</option>
                      <option value="VALID">Valid Claim</option>
                    </select>
                  </div>

                  {/* Issue Category */}
                  <div>
                    <label className={`block text-sm font-medium ${theme.textSecondary} mb-2`}>
                      Issue Category
                    </label>
                    <select
                      value={issueCategory}
                      onChange={(e) => setIssueCategory(e.target.value)}
                      className={`w-full px-3 py-2 ${theme.input} rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none`}
                    >
                      <option value="">Select category...</option>
                      <option value="UPCODING">Upcoding</option>
                      <option value="UNBUNDLING">Unbundling</option>
                      <option value="MEDICAL_NECESSITY">Medical Necessity</option>
                      <option value="DUPLICATE">Duplicate Service</option>
                      <option value="ELIGIBILITY">Eligibility Issue</option>
                      <option value="DOCUMENTATION">
                        Documentation Insufficient
                      </option>
                      <option value="OTHER">Other</option>
                    </select>
                  </div>

                  {/* Confidence */}
                  <div>
                    <label className={`block text-sm font-medium ${theme.textSecondary} mb-2`}>
                      Confidence Level
                    </label>
                    <div className="flex gap-2">
                      {['HIGH', 'MEDIUM', 'LOW'].map((level) => (
                        <button
                          key={level}
                          onClick={() => setConfidence(level)}
                          className={`flex-1 py-2 text-sm font-medium rounded-lg border transition ${
                            confidence === level
                              ? 'border-blue-500 bg-blue-50 dark:bg-blue-500/20 text-blue-700 dark:text-blue-400'
                              : `border ${theme.border} hover:border-slate-300 dark:hover:border-slate-600 ${theme.textSecondary}`
                          }`}
                        >
                          {level}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Overpayment Amount */}
                  {decision === 'DENY' && (
                    <div>
                      <label className={`block text-sm font-medium ${theme.textSecondary} mb-2`}>
                        Confirmed Overpayment Amount
                      </label>
                      <div className="relative">
                        <span className={`absolute left-3 top-1/2 -translate-y-1/2 ${theme.textTertiary}`}>
                          $
                        </span>
                        <input
                          type="text"
                          value={overpaymentAmount}
                          onChange={(e) => setOverpaymentAmount(e.target.value)}
                          placeholder="0.00"
                          className={`w-full pl-8 pr-4 py-2 ${theme.input} rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none`}
                        />
                      </div>
                    </div>
                  )}

                  {/* Notes */}
                  <div>
                    <label className={`block text-sm font-medium ${theme.textSecondary} mb-2`}>
                      Notes & Rationale
                    </label>
                    <textarea
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      rows={4}
                      placeholder="Document your findings and reasoning..."
                      className={`w-full px-3 py-2 ${theme.input} rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none resize-none`}
                    />
                  </div>

                  {/* Quick Phrases */}
                  <div>
                    <p className={`text-xs ${theme.textTertiary} mb-2`}>Quick phrases:</p>
                    <div className="flex flex-wrap gap-2">
                      {[
                        'Documentation verified',
                        'Medical necessity not supported',
                        'Refer for investigation',
                      ].map((phrase) => (
                        <button
                          key={phrase}
                          onClick={() =>
                            setNotes(notes + (notes ? ' ' : '') + phrase)
                          }
                          className={`text-xs px-2 py-1 ${theme.bgTertiary} ${theme.textSecondary} rounded ${theme.bgHover} transition`}
                        >
                          {phrase}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Submit Button */}
                  <button
                    onClick={handleSubmitDecision}
                    disabled={!decision || isSubmitting}
                    className="w-full bg-blue-600 text-white py-3 rounded-lg font-medium hover:bg-blue-700 focus:ring-4 focus:ring-blue-200 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {isSubmitting ? (
                      <>
                        <svg
                          className="animate-spin h-4 w-4 text-white"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          />
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          />
                        </svg>
                        Submitting...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4" />
                        Submit Decision
                      </>
                    )}
                  </button>

                  {/* Keyboard Shortcuts Hint */}
                  <p className={`text-xs text-center ${theme.textMuted}`}>
                    Press Ctrl+Enter to submit
                  </p>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
      <Footer />
    </div>
  );
};

export default CaseDetailView;
